import { logger } from "@/lib/logger"

const log = logger("Timezone")

const TIMEZONE_ALIASES: Record<string, string> = {
  "Asia/Calcutta": "Asia/Kolkata",
}

export function canonicalizeTimezone(timezone: string): string {
  return TIMEZONE_ALIASES[timezone] ?? timezone
}

/**
 * Get the user's browser timezone
 * @returns IANA timezone identifier (e.g., "America/New_York")
 */
export function getUserTimezone(): string {
  return canonicalizeTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
}

/**
 * Convert 12-hour time format to 24-hour format
 * @param time - Time string in HH:MM format (12-hour)
 * @param ampm - AM or PM
 * @returns Time string in HH:MM format (24-hour)
 */
export function convertTo24Hour(time: string, ampm: "AM" | "PM"): string {
  const [hourStr, minute] = time.split(":")
  let hour = parseInt(hourStr, 10)

  if (ampm === "PM" && hour !== 12) {
    hour += 12
  } else if (ampm === "AM" && hour === 12) {
    hour = 0
  }

  return `${hour.toString().padStart(2, "0")}:${minute}`
}

/**
 * Convert 24-hour time format to 12-hour format with AM/PM
 * @param time - Time string in HH:MM format (24-hour)
 * @returns Object with time string and AM/PM
 */
export function convertTo12Hour(time: string): {
  time: string
  ampm: "AM" | "PM"
} {
  const [hourStr, minute] = time.split(":")
  let hour = parseInt(hourStr, 10)
  const ampm: "AM" | "PM" = hour >= 12 ? "PM" : "AM"

  if (hour === 0) {
    hour = 12
  } else if (hour > 12) {
    hour -= 12
  }

  return {
    time: `${hour}:${minute}`,
    ampm,
  }
}

/**
 * Get timezone abbreviation for display (e.g., PST, EST)
 * @param timezone - IANA timezone identifier
 * @param date - Date to check (for DST-aware abbreviation)
 * @returns Timezone abbreviation
 */
export function getTimezoneAbbreviation(
  timezone?: string,
  date: Date = new Date(),
): string {
  const tz = canonicalizeTimezone(timezone || getUserTimezone())

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    })

    const parts = formatter.formatToParts(date)
    const timeZonePart = parts.find((part) => part.type === "timeZoneName")

    return timeZonePart?.value || tz
  } catch (error) {
    log.error("Error getting timezone abbreviation", error)
    return tz
  }
}

/**
 * Get full timezone display name (e.g., "Pacific Standard Time")
 * @param timezone - IANA timezone identifier
 * @param date - Date to check (for DST-aware name)
 * @returns Full timezone name
 */
export function getTimezoneName(
  timezone?: string,
  date: Date = new Date(),
): string {
  const tz = canonicalizeTimezone(timezone || getUserTimezone())

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "long",
    })

    const parts = formatter.formatToParts(date)
    const timeZonePart = parts.find((part) => part.type === "timeZoneName")

    return timeZonePart?.value || tz
  } catch (error) {
    log.error("Error getting timezone name", error)
    return tz
  }
}

/**
 * Format days of week for display
 * @param days - Array of day numbers (0=Sunday, 6=Saturday)
 * @returns Formatted string (e.g., "daily", "weekdays", "Mon, Wed, Fri")
 */
export function formatDaysOfWeek(days: number[] | undefined): string {
  if (!days || days.length === 0) {
    return "daily"
  }

  if (days.length === 7) {
    return "daily"
  }

  // Check if it's weekdays (Mon-Fri)
  const weekdays = [1, 2, 3, 4, 5]
  if (days.length === 5 && weekdays.every((day) => days.includes(day))) {
    return "weekdays"
  }

  // Check if it's weekends
  const weekends = [0, 6]
  if (days.length === 2 && weekends.every((day) => days.includes(day))) {
    return "weekends"
  }

  // Format specific days
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
  const sortedDays = [...days].sort((a, b) => a - b)

  return sortedDays.map((day) => dayNames[day]).join(", ")
}

/**
 * Convert a scheduled time to display format in user's local timezone
 * This is for displaying scheduled times that are stored with a specific timezone
 * @param scheduledTime - Time in HH:MM format (24-hour)
 * @param storedTimezone - The timezone the schedule was created in
 * @param recurrenceType - "daily" or "weekly"
 * @param daysOfWeek - Array of day numbers for weekly schedules
 * @returns Formatted schedule string (e.g., "9:00 AM daily" or "9:00 AM Mon, Wed, Fri")
 */
export function formatScheduleTime(
  scheduledTime: string,
  storedTimezone: string,
  recurrenceType?: string,
  daysOfWeek?: number[],
): string {
  try {
    // Convert to 12-hour format
    const { time, ampm } = convertTo12Hour(scheduledTime)

    // Format the days
    const daysStr =
      recurrenceType === "weekly" ? formatDaysOfWeek(daysOfWeek) : "daily"

    // Get timezone abbreviation
    const tzAbbr = getTimezoneAbbreviation(storedTimezone)

    return `${time} ${ampm} ${daysStr} (${tzAbbr})`
  } catch (error) {
    log.error("Error formatting schedule time", error)
    return scheduledTime
  }
}

/**
 * Validate IANA timezone identifier
 * @param timezone - Timezone string to validate
 * @returns true if valid, false otherwise
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: canonicalizeTimezone(timezone) })
    return true
  } catch {
    return false
  }
}
